/**
 * DevAutoLogin
 *
 * Dev-Komfort-Komponente: wenn das Frontend lokal in `vite dev` läuft
 * UND kein Token im localStorage ist UND ein VITE_DEV_SSO_SECRET in der
 * .env.local hinterlegt ist, holt sie automatisch einen frischen SSO-Token
 * für `kiki-test-001 / luca-test` und logged ein.
 *
 * Sicherheitsbedingungen (Mehrfach-Gates, damit das nie in Production läuft):
 *   - `import.meta.env.DEV === true` (nur in vite dev mode, NICHT in build)
 *   - `window.location.hostname` ist 'localhost' / '127.0.0.1'
 *   - `VITE_DEV_SSO_SECRET` env-var muss gesetzt sein (.env.local — nicht gitignored)
 *
 * Sobald eine dieser Bedingungen nicht stimmt → Komponente macht nichts.
 * Wenn der User bereits eingeloggt ist → macht auch nichts.
 *
 * Production-Builds enthalten die Komponente zwar im Bundle, aber `DEV`
 * ist `false` und das Secret fehlt → komplett inaktiv.
 */

import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

function isLocalDev() {
  if (!import.meta.env.DEV) return false;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local');
}

export default function DevAutoLogin() {
  const { user, loading } = useAuth();
  const triedRef = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (user) return;
    if (triedRef.current) return;

    if (!isLocalDev()) return;

    const secret = import.meta.env.VITE_DEV_SSO_SECRET;
    const orgId = import.meta.env.VITE_DEV_HEYKIKI_ORG_ID || 'kiki-test-001';
    const userId = import.meta.env.VITE_DEV_HEYKIKI_USER_ID || 'luca-test';
    if (!secret) return;

    triedRef.current = true;

    (async () => {
      try {
        // eslint-disable-next-line no-console
        console.info('[DevAutoLogin] Hole SSO-Token für', userId);
        const r = await axios.post(
          `${API_URL}/heykiki/sso-token`,
          { heykikiOrgId: orgId, heykikiUserId: userId, userName: 'Dev Auto-Login' },
          { headers: { 'X-HeyKiki-Secret': secret } }
        );
        const token = r?.data?.token;
        if (!token) {
          console.warn('[DevAutoLogin] kein Token in der Response');
          return;
        }
        sessionStorage.clear();
        localStorage.setItem('token', token);
        // Hard-reload damit AuthContext den Token frisch picked.
        window.location.reload();
      } catch (e) {
        console.warn('[DevAutoLogin] fehlgeschlagen:', e.response?.data?.error || e.message);
      }
    })();
  }, [user, loading]);

  return null;
}
